/**
 * Cross-calc value bus — the "nervous system" that lets one calc
 * feed its outputs into a downstream calc.
 *
 * Session-scoped: persisted via AsyncStorage under a single key,
 * cleared by calling clearBus(). Values stored in Imperial base
 * units (see src/utils/slots.ts); screens convert on read/write
 * through their useUnits() helpers.
 *
 * Lookup semantics: latestForSlot() returns the most recently
 * written value for a given slot. Writes are de-duped per-route
 * (a second write from the same calc replaces its previous entry).
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import type { Slot } from './slots';

const KEY = 'tabai.valueBus.v1';

export type BusEntry = {
  /** Calc route that produced these values (navigator Stack.Screen name). */
  route: string;
  /** Human-readable title — cached so we don't have to look it up later. */
  title: string;
  /** Slot → Imperial base-unit numeric value. */
  slots: Partial<Record<Slot, number>>;
  /** Epoch ms when this entry was written. */
  ts: number;
};

let memory: BusEntry[] = [];
let hydrated = false;
const listeners = new Set<() => void>();

async function hydrate(): Promise<void> {
  if (hydrated) return;
  try {
    const raw = await AsyncStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) memory = parsed as BusEntry[];
    }
  } catch (e) {
    console.warn('[valueBus] hydrate failed:', e);
    // corrupt payload — start fresh
    memory = [];
  }
  hydrated = true;
}

async function persist(): Promise<void> {
  try {
    await AsyncStorage.setItem(KEY, JSON.stringify(memory));
  } catch (e) {
    console.warn('[valueBus] persist failed:', e);
    // non-fatal — in-memory state still correct
  }
}

function notify() {
  for (const l of listeners) l();
}

/** Called on app start (or lazily) to load persisted entries. */
export async function ensureBusReady(): Promise<void> {
  await hydrate();
}

/**
 * Write (or replace) this calc's contribution to the bus.
 * Values with `undefined` are ignored, so callers can pass a
 * map keyed by every slot the calc could emit — only defined
 * ones land on the bus.
 */
export async function writeBusEntry(
  route: string,
  title: string,
  slots: Partial<Record<Slot, number | undefined>>,
): Promise<void> {
  await hydrate();
  const cleaned: Partial<Record<Slot, number>> = {};
  for (const [k, v] of Object.entries(slots)) {
    if (typeof v === 'number' && Number.isFinite(v)) {
      (cleaned as Record<string, number>)[k] = v;
    }
  }
  if (Object.keys(cleaned).length === 0) {
    // Nothing to record — but if there was a previous entry for this
    // route, leave it (user might have cleared fields transiently).
    return;
  }
  const idx = memory.findIndex((e) => e.route === route);
  const entry: BusEntry = { route, title, slots: cleaned, ts: Date.now() };
  if (idx >= 0) {
    memory.splice(idx, 1);
  }
  memory.push(entry);
  // Cap at 50 entries to keep storage bounded.
  if (memory.length > 50) memory = memory.slice(-50);
  await persist();
  notify();
}

/** Most recently-written value for a slot, or null if nothing has ever been written. */
export function latestForSlot(
  slot: Slot,
): { value: number; fromRoute: string; fromTitle: string; ts: number } | null {
  for (let i = memory.length - 1; i >= 0; i--) {
    const e = memory[i];
    const v = e.slots[slot];
    if (typeof v === 'number') {
      return { value: v, fromRoute: e.route, fromTitle: e.title, ts: e.ts };
    }
  }
  return null;
}

/** Full ordered bus — oldest first. Used for the breadcrumb / trail UI. */
export function getBusEntries(): BusEntry[] {
  return memory.slice();
}

/** Wipe everything. */
export async function clearBus(): Promise<void> {
  memory = [];
  try {
    await AsyncStorage.removeItem(KEY);
  } catch (e) {
    console.warn('[valueBus] clearBus failed:', e);
    // non-fatal
  }
  notify();
}

/** Drop the entry emitted by a single calc (used by "clear prefill" UX). */
export async function clearRouteEntry(route: string): Promise<void> {
  await hydrate();
  const next = memory.filter((e) => e.route !== route);
  if (next.length === memory.length) return;
  memory = next;
  await persist();
  notify();
}

/** Subscribe to bus mutations. Returns an unsubscribe fn. */
export function subscribeBus(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
